import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { ConfigRelease, forexApi } from "@/lib/forex-api";

interface ReleaseRecord {
  id: string;
  configType: string;
  configTypeLabel: string;
  configId: string;
  version: string;
  env: string;
  status: "success" | "rolling_back" | "failed";
  publisher: string;
  publishTime: string;
  affectedObjects: string;
}

const configTypeLabel: Record<string, string> = {
  app_terms: "应用条款",
  feature_flags: "功能开关",
  market_config: "市场配置",
  symbol_config: "品种配置",
  follow_rule_config: "跟单规则",
  risk_config: "风控配置",
  notification_config: "通知配置",
  app_compliance_copy: "合规文案",
  query_policy: "查询策略",
  profile_config: "个人中心配置",
  security_config: "安全配置",
};

function formatDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function mapRelease(release: ConfigRelease): ReleaseRecord {
  return {
    id: release.release_id,
    configType: release.config_key,
    configTypeLabel: configTypeLabel[release.config_key] || release.config_key,
    configId: release.config_key,
    version: release.version,
    env: "production",
    status: release.status === "rolled_back" ? "rolling_back" : release.status === "active" || release.status === "superseded" ? "success" : "failed",
    publisher: "admin",
    publishTime: formatDateTime(release.published_at),
    affectedObjects: release.description || "GET /app/config/bootstrap 下次拉取生效",
  };
}

const statusChip: Record<string, { cls: string; label: string }> = {
  success: { cls: "bg-emerald-500/10 text-emerald-500", label: "发布成功" },
  rolling_back: { cls: "bg-amber-500/10 text-amber-500", label: "已回滚" },
  failed: { cls: "bg-red-500/10 text-red-500", label: "发布失败" },
};

const typeChip: Record<string, { cls: string }> = {
  risk_config: { cls: "bg-red-500/10 text-red-500" },
  symbol_config: { cls: "bg-brand/10 text-brand" },
  query_policy: { cls: "bg-emerald-500/10 text-emerald-500" },
  app_terms: { cls: "bg-amber-500/10 text-amber-500" },
  app_compliance_copy: { cls: "bg-amber-500/10 text-amber-500" },
};

export default function ConfigReleasesPage() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState<ReleaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const showToast = (type: "success" | "error", msg: string) => { setToast({ type, msg }); setTimeout(() => setToast(null), 2800); };

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const data = await forexApi.listConfigReleases();
        if (mounted) setReleases(data.items.map(mapRelease));
      } catch (error) {
        if (mounted) showToast("error", error instanceof Error ? error.message : "配置发布记录加载失败");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const handleRollback = async (releaseId: string) => {
    try {
      const updated = await forexApi.rollbackConfigRelease(releaseId);
      setReleases((prev) => prev.map((item) => (item.id === releaseId ? mapRelease(updated) : item)));
      showToast("success", `${releaseId} 已回滚`);
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "回滚失败");
    }
  };

  const filtered = releases.filter((r) => {
    if (filterType && r.configType !== filterType) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "报表与审计", href: "/forex/audit" }, { label: "配置发布记录" }]} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">配置发布记录</h2>
          <p className="text-xs text-text-muted mt-0.5">所有配置类型的发布/回滚记录，支持查看变更 diff、影响范围、回滚入口和审计链</p>
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回</button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部配置类型</option>
          <option value="app_terms">应用条款</option>
          <option value="feature_flags">功能开关</option>
          <option value="symbol_config">品种配置</option>
          <option value="risk_config">风控配置</option>
          <option value="query_policy">查询策略</option>
          <option value="app_compliance_copy">合规文案</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部状态</option>
          <option value="success">发布成功</option>
          <option value="rolling_back">已回滚</option>
          <option value="failed">发布失败</option>
        </select>
        <span className="text-[11px] text-text-muted ml-auto">{filtered.length} 条记录</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">发布 ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">配置类型</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">配置 ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">版本</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">环境</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[10px]">发布状态</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">发布人</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">发布时间</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[10px]">影响范围</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[10px]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-text-muted text-xs">正在加载配置发布记录...</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-text-muted text-xs">暂无发布记录</td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className={`border-b border-surface-border hover:bg-surface/30 ${r.status === "failed" ? "bg-red-500/[0.02]" : ""}`}>
                  <td className="px-4 py-2.5 text-text-primary font-mono text-[11px]">{r.id}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${typeChip[r.configType]?.cls || "bg-text-muted/10 text-text-secondary"}`}>{r.configTypeLabel}</span>
                  </td>
                  <td className="px-4 py-2.5 text-text-primary font-mono text-[11px]">{r.configId}</td>
                  <td className="px-4 py-2.5 text-text-primary font-mono text-[11px] font-medium">{r.version}</td>
                  <td className="px-4 py-2.5"><span className="text-xs text-text-secondary">{r.env === "production" ? "生产环境" : r.env === "staging" ? "预发布" : r.env}</span></td>
                  <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusChip[r.status].cls}`}><i className={`text-[8px] ${r.status === "success" ? "ri-checkbox-circle-fill" : r.status === "rolling_back" ? "ri-history-fill" : "ri-close-circle-fill"}`} />{statusChip[r.status].label}</span></td>
                  <td className="px-4 py-2.5 text-text-primary text-xs">{r.publisher}</td>
                  <td className="px-4 py-2.5 text-text-muted text-[11px] whitespace-nowrap">{r.publishTime}</td>
                  <td className="px-4 py-2.5 text-text-secondary text-[11px] max-w-[280px] truncate" title={r.affectedObjects}>{r.affectedObjects}</td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <Link to={`/forex/config/releases/detail?id=${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap cursor-pointer"><i className="ri-eye-line text-xs" />详情</Link>
                      {r.status === "success" && <button onClick={() => handleRollback(r.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 whitespace-nowrap cursor-pointer"><i className="ri-history-line text-xs" />回滚</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (<div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]"><div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}><i className={`text-base ${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`} />{toast.msg}</div></div>)}
    </div>
  );
}
